import React from "react";
import axios from "axios";
import {apiUploadCompsUrl, apiUploadCompsStatusUrl, apiUploadCompsCommitUrl} from '../../requests/apiBaseUrls';
import Modal from "../Modal/Modal";

// Header list for the CoStar comps CSV (40 columns)
var SAMPLE_COMPS_CSV_HEADERS = [
    'Sale Date',
    'Property Address',
    'Property Name',
    'Property City',
    'Property State',
    'Property Zip Code',
    'Property Type',
    'Tenancy',
    'Secondary Type',
    'Year Built',
    'Building SF',
    'Land Area AC',
    'Sale Price',
    'Price Per SF',
    'Actual Cap Rate',
    'Asking Price',
    'Assessed Value',
    'Assessed Year',
    'Buyer (True) Contact',
    'Buyer (True) Company',
    'Buyer (True) Phone',
    'Buyer (True) Address',
    'Buyer (True) City',
    'Buyer (True) State',
    'Buyer (True) Zip Code',
    'Buyers Broker Agent First Name',
    'Buyers Broker Agent Last Name',
    'Buyers Broker Company',
    'Seller (True) Contact',
    'Seller (True) Company',
    'Seller (True) Phone',
    'Seller (True) Address',
    'Seller (True) City',
    'Seller (True) State',
    'Seller (True) Zip Code',
    'Listing Broker Agent First Name',
    'Listing Broker Agent Last Name',
    'Listing Broker Company',
    'Latitude',
    'Longitude',
];

var FILE_EXTENSION_REGEX = /\.csv$/i;
var POLL_INTERVAL_MS = 2000;
var BASE64_PREFIX_DELIMITER = 'base64,';

// Phase names tracked on this component (client-side); they bundle together several
// server statuses so the UI stays simple.
var PHASE_IDLE = 'idle';
var PHASE_UPLOADING = 'uploading';
var PHASE_PROCESSING = 'processing';   // server is parsing + matching
var PHASE_PREVIEW = 'preview';         // server returned ready_for_review
var PHASE_COMMITTING = 'committing';   // commit in flight
var PHASE_COMPLETE = 'complete';
var PHASE_FAILED = 'failed';

var SERVER_STATUS_MATCHING = 'matching';
var SERVER_STATUS_READY = 'ready_for_review';
var SERVER_STATUS_COMMITTING = 'committing';
var SERVER_STATUS_WRITING_NOTES = 'writing_notes';
var SERVER_STATUS_FILING_OWNERS = 'filing_owners';
var SERVER_STATUS_COMPLETE = 'complete';
var SERVER_STATUS_FAILED = 'failed';

var ACTION_INSERT = 'insert';
var ACTION_UPDATE = 'update';
var ACTION_SKIP = 'skip';

var OWNER_CODE_MATCHED = 'matched_existing';
var OWNER_CODE_NO_MATCH = 'no_match';
var OWNER_CODE_AMBIGUOUS = 'ambiguous';
var OWNER_CODE_NO_NAME = 'no_name';
var SELLER_CODE_WILL_CREATE = 'will_create_seller';
var SELLER_CODE_MATCHED = 'matched_existing';
var SELLER_CODE_GUARD = 'seller_guard_conflict';

export default class UploadCompsTab extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            file: {
                name: null,
                encryption: null,
                message: '',
                invalidUpload: false,
            },
            phase: PHASE_IDLE,
            jobId: null,
            progress: 0,
            serverStatus: null,
            result: null,
            showHelp: false,
        };

        this._pollTimer = null;
        this.uploadFile = this.uploadFile.bind(this);
        this.removeFile = this.removeFile.bind(this);
        this.sendFile = this.sendFile.bind(this);
        this.commit = this.commit.bind(this);
        this.resetUpload = this.resetUpload.bind(this);
        this.downloadSampleCsv = this.downloadSampleCsv.bind(this);
        this.downloadSummary = this.downloadSummary.bind(this);
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    stopPolling() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    downloadSampleCsv() {
        this.downloadTextFile(SAMPLE_COMPS_CSV_HEADERS.join(',') + '\n', 'sample-comps.csv', 'text/csv;charset=utf-8;');
    }

    downloadTextFile(text, filename, mime) {
        var blob = new Blob([text], { type: mime || 'text/plain;charset=utf-8;' });
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    /**
     * Build a plain-text post-commit audit summary from the job result.
     * The server fills in plan[i].outcome and resultingPropertyId during commit,
     * so this report cites the actual DB ids of inserted/updated rows.
     */
    buildSummaryText() {
        var r = this.state.result || {};
        var cs = r.commitStats || {};
        var stats = r.stats || {};
        var plan = r.plan || [];
        var rejected = r.rejected || [];
        var valid = r.valid || [];

        var lines = [];
        function push(s) { lines.push(s == null ? '' : s); }
        function heading(title, underline) {
            push('');
            push(title);
            push(new Array(title.length + 1).join(underline || '-'));
        }
        function pad(label) {
            var width = 18;
            return (label + ':' + new Array(width).join(' ')).slice(0, width);
        }
        function rowLabel(p) {
            var name = p.name || '(no name)';
            var addr = p.address || '(no address)';
            var city = p.city || '(no city)';
            return 'Row ' + p.rowIndex + ': ' + name + ' — ' + addr + ', ' + city;
        }
        function formatExisting(list) {
            if (!list || !list.length) return '';
            return list.map(function (e) {
                var bits = ['id ' + e.id];
                if (e.source) bits.push(e.source);
                if (e.status) bits.push(e.status);
                if (e.closeOfEscrow) bits.push('COE ' + e.closeOfEscrow);
                return bits.join(' / ');
            }).join('; ');
        }

        push('iCLIMB Comps Upload Summary');
        push('===========================');
        push('File:        ' + (r.fileName || this.state.file.name || 'unknown.csv'));
        push('Date:        ' + new Date().toLocaleString());
        push('Job ID:      ' + (this.state.jobId || '(none)'));

        heading('OVERALL');
        push(pad('Total rows')     + (r.total || 0));
        push(pad('Valid')          + (r.validCount || 0));
        push(pad('Rejected')       + (r.rejectedCount || 0));
        push(pad('Warnings')       + (r.warningsCount || 0));

        heading('PLAN');
        push(pad('Will insert')    + (stats.insertCount || 0));
        push(pad('Will update')    + (stats.updateCount || 0));
        push(pad('Will skip')      + (stats.skipCount || 0));

        if (stats.buyerNeedOwnerCount || stats.buyerMatchedCount) {
            heading('BUYER RESOLUTION (preview)');
            push(pad('Buyers matched')     + (stats.buyerMatchedCount || 0));
            push(pad('Need owner')         + (stats.buyerNeedOwnerCount || 0));
            push(pad('Not matched')        + (stats.buyerNotFoundCount || 0));
            push(pad('Several match')      + (stats.buyerAmbiguousCount || 0));
            push(pad('No buyer in file')   + (stats.buyerNoContactCount || 0));
        }
        if (stats.sellerOwnerMatchedCount || stats.sellerOwnerNotFoundCount) {
            heading('PAST OWNER RESOLUTION (preview)');
            push(pad('Sellers matched')    + (stats.sellerOwnerMatchedCount || 0));
            push(pad('Not matched')        + (stats.sellerOwnerNotFoundCount || 0));
        }
        if (stats.sellerWillCreateCount || stats.sellerMatchedCount) {
            heading('SELLER RESOLUTION (preview)');
            push(pad('Sellers matched')    + (stats.sellerMatchedCount || 0));
            push(pad('New sellers')        + (stats.sellerWillCreateCount || 0));
            push(pad('Seller conflicts')   + (stats.sellerGuardConflictCount || 0));
        }

        var buyerPreviewRows = plan.filter(function (p) { return p.buyerResolution && p.buyerResolution.message; });
        if (buyerPreviewRows.length) {
            heading('BUYER DETAIL BY ROW');
            buyerPreviewRows.forEach(function (p) {
                push(rowLabel(p));
                push('  Buyer: ' + p.buyerResolution.message);
                if (p.sellerOwnerResolution && p.sellerOwnerResolution.message) {
                    push('  Seller: ' + p.sellerOwnerResolution.message);
                }
            });
        }

        heading('COMMIT RESULT');
        push(pad('Inserted')       + (cs.inserted || 0));
        push(pad('Updated')        + (cs.updated || 0));
        push(pad('Notes written')  + (cs.notesWritten || 0));
        push(pad('Owners filed')   + (cs.ownersFiled || 0));
        push(pad('Errors')         + ((cs.errors && cs.errors.length) || 0));

        if (rejected.length) {
            heading('REJECTED ROWS (not part of the plan)');
            rejected.forEach(function (rr) {
                push(rowLabel(rr));
                (rr.reasons || []).forEach(function (reason) { push('  - ' + reason); });
                (rr.warnings || []).forEach(function (w) { push('  ! ' + w); });
            });
        }

        var skipped = plan.filter(function (p) { return p.action === ACTION_SKIP; });
        if (skipped.length) {
            heading('SKIPPED (did not write)');
            skipped.forEach(function (p) {
                push(rowLabel(p));
                push('  Reason: ' + p.reason);
                var existing = formatExisting(p.existingSummary);
                if (existing) push('  Matched: ' + existing);
            });
        }

        var inserted = plan.filter(function (p) { return p.action === ACTION_INSERT; });
        if (inserted.length) {
            heading('INSERTED');
            inserted.forEach(function (p) {
                var id = p.resultingPropertyId;
                var suffix = p.outcome === 'inserted' && id ? ' → new property id ' + id
                           : p.outcome === 'error'        ? ' → ERROR (not inserted)'
                           :                                ' → (no id recorded)';
                push(rowLabel(p) + suffix);
                if (p.noteWritten) push('  (note written to PROPERTY_NOTES)');
                else if (p.hasNote && p.outcome === 'inserted') push('  (note was queued; see commit errors)');
            });
        }

        var updated = plan.filter(function (p) { return p.action === ACTION_UPDATE; });
        if (updated.length) {
            heading('UPDATED');
            updated.forEach(function (p) {
                var id = p.resultingPropertyId || p.targetRowId;
                var suffix = p.outcome === 'updated' ? ' → updated property id ' + id
                           : p.outcome === 'error'   ? ' → ERROR (not updated; target id ' + p.targetRowId + ')'
                           :                           ' → (target id ' + p.targetRowId + ')';
                push(rowLabel(p) + suffix);
                if (p.noteWritten) push('  (note added to PROPERTY_NOTES)');
                else if (p.hasNote && p.outcome === 'updated') push('  (note was queued; see commit errors)');
            });
        }

        var warningRows = valid.filter(function (v) { return v.warnings && v.warnings.length > 0; });
        if (warningRows.length) {
            heading('FIELD WARNINGS');
            warningRows.forEach(function (v) {
                push('Row ' + v.rowIndex + ': ' + (v.name || '(no name)') + ' — ' + (v.address || '') + ', ' + (v.city || ''));
                v.warnings.forEach(function (w) { push('  - ' + w); });
            });
        }

        var commitErrors = cs.errors || [];
        if (commitErrors.length) {
            heading('ERRORS DURING COMMIT');
            commitErrors.forEach(function (e) {
                var loc = e.phase ? (e.phase + (e.batch ? ' batch ' + e.batch : '')
                    + (e.rowStart ? ' rows ' + e.rowStart + '-' + e.rowEnd : '')) : '';
                push((loc ? loc + ': ' : '') + (e.error || JSON.stringify(e)));
            });
        }

        push('');
        push('--- end of report ---');
        return lines.join('\n');
    }

    downloadSummary() {
        var text = this.buildSummaryText();
        var now = new Date();
        var stamp = now.getFullYear()
            + String(now.getMonth() + 1).padStart(2, '0')
            + String(now.getDate()).padStart(2, '0')
            + '-'
            + String(now.getHours()).padStart(2, '0')
            + String(now.getMinutes()).padStart(2, '0');
        this.downloadTextFile(text, 'comps-upload-summary-' + stamp + '.txt');
    }

    uploadFile() {
        var self = this;
        var input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', '.csv');
        input.setAttribute('name', 'file');
        input.click();

        input.addEventListener('change', function () {
            var file = input.files[0];
            if (!file) return;

            if (!FILE_EXTENSION_REGEX.test(file.name)) {
                self.setState({
                    file: { name: null, encryption: null, message: 'Wrong file format! Supported file type: .csv', invalidUpload: true },
                });
                return;
            }

            var reader = new FileReader();
            reader.addEventListener('load', function (event) {
                var dataUrl = event.target.result || '';
                var idx = dataUrl.indexOf(BASE64_PREFIX_DELIMITER);
                var encryption = idx >= 0 ? dataUrl.slice(idx + BASE64_PREFIX_DELIMITER.length) : '';
                self.setState({
                    file: { name: file.name, encryption: encryption, message: '', invalidUpload: false },
                });
            });
            reader.addEventListener('error', function () {
                self.setState({
                    file: { name: file.name, encryption: null, message: 'Could not read file.', invalidUpload: true },
                });
            });
            reader.readAsDataURL(file);
        });
    }

    removeFile() {
        this.stopPolling();
        this.setState({
            file: { name: null, encryption: null, message: '', invalidUpload: false },
            phase: PHASE_IDLE,
            jobId: null,
            progress: 0,
            serverStatus: null,
            result: null,
        });
    }

    resetUpload() {
        this.removeFile();
    }

    /**
     * Route server status → client phase and store the latest result payload.
     * Stops polling once we reach a resting state (preview, complete, failed).
     */
    handleStatusResponse(data) {
        var status = data.status;
        var phase = this.state.phase;
        console.log('[Comps Upload] Status update:', status, 'progress:', data.progress, '(phase:', phase + ')');
        this.setState({ progress: data.progress || 0, serverStatus: status, result: data });

        if (status === SERVER_STATUS_READY) {
            // Only move to preview if we're in the initial processing phase.
            // If we're in PHASE_COMMITTING, this is a stale/race-condition poll — ignore it.
            if (phase === PHASE_PROCESSING) {
                this.stopPolling();
                console.log('[Comps Upload] Dry-run complete, ready for review');
                this.setState({ phase: PHASE_PREVIEW });
            } else {
                console.warn('[Comps Upload] Got ready_for_review during phase', phase, '— ignoring (race condition)');
            }
        } else if (status === SERVER_STATUS_COMPLETE) {
            this.stopPolling();
            console.log('[Comps Upload] Commit complete');
            this.setState({ phase: PHASE_COMPLETE });
        } else if (status === SERVER_STATUS_FAILED) {
            this.stopPolling();
            console.error('[Comps Upload] Job failed:', data.errors);
            this.setState({ phase: PHASE_FAILED });
        }
        // parsing / matching / committing / writing_notes → keep polling, don't touch phase.
    }

    pollJobStatus(jobId) {
        var self = this;
        console.log('[Comps Upload] Starting poll for job', jobId);
        this._pollTimer = setInterval(function () {
            axios.get(apiUploadCompsStatusUrl + '/' + jobId)
                .then(function (res) { 
                    self.handleStatusResponse(res.data); 
                })
                .catch(function (err) { 
                    console.error('[Comps Upload] Poll error:', err);
                    // Don't stop polling on network errors - keep trying
                    if (err.response && err.response.status === 404) {
                        console.error('[Comps Upload] Job not found - stopping poll');
                        self.stopPolling();
                        self.setState({ 
                            phase: PHASE_FAILED,
                            result: { status: SERVER_STATUS_FAILED, errors: [{ error: 'Job not found' }] }
                        });
                    }
                });
        }, POLL_INTERVAL_MS);
    }

    extractHttpErrorMessage(err) {
        if (!err) return 'Unspecified error';
        if (err.response) {
            var data = err.response.data;
            if (data && typeof data === 'object') return data.error || data.message || JSON.stringify(data);
            if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data;
            return 'HTTP ' + err.response.status + ' ' + (err.response.statusText || 'Error');
        }
        return err.message || 'Unspecified error';
    }

    sendFile() {
        var self = this;
        this.setState({
            phase: PHASE_UPLOADING,
            progress: 0,
            serverStatus: null,
            result: null,
            jobId: null,
        });

        axios({
            method: 'post',
            url: apiUploadCompsUrl,
            headers: { 'Accept': '*' },
            data: { file_name: this.state.file.name, encryption: this.state.file.encryption },
        }).then(function (res) {
            if (res.data && res.data.jobId) {
                self.setState({ phase: PHASE_PROCESSING, jobId: res.data.jobId, serverStatus: SERVER_STATUS_MATCHING });
                self.pollJobStatus(res.data.jobId);
            } else {
                self.setState({
                    phase: PHASE_FAILED,
                    result: { status: SERVER_STATUS_FAILED, errors: [{ error: 'Unexpected server response' }] },
                });
            }
        }, function (err) {
            var errorMessage = self.extractHttpErrorMessage(err);
            console.error('[Comps Upload] Error:', errorMessage, err);
            self.setState({
                phase: PHASE_IDLE,
                file: { name: null, encryption: null, message: errorMessage, invalidUpload: true },
            });
        });
    }

    commit() {
        var self = this;
        var jobId = this.state.jobId;
        if (!jobId) {
            console.error('[Comps Upload] Cannot commit: no jobId');
            return;
        }
        
        // Stop any existing polling before starting commit
        this.stopPolling();
        
        this.setState({ phase: PHASE_COMMITTING, progress: 0, serverStatus: SERVER_STATUS_COMMITTING });
        console.log('[Comps Upload] Starting commit for job', jobId);

        axios.post(apiUploadCompsCommitUrl + '/' + jobId + '/commit').then(function (res) {
            console.log('[Comps Upload] Commit accepted, starting poll', res.data);
            self.pollJobStatus(jobId);
        }, function (err) {
            var errorMessage = self.extractHttpErrorMessage(err);
            console.error('[Comps Upload] Commit request failed:', errorMessage, err);
            self.setState({
                phase: PHASE_FAILED,
                result: { status: SERVER_STATUS_FAILED, errors: [{ error: errorMessage }] },
            });
        });
    }

    // ─── Render helpers ────────────────────────────────────────────

    renderProgressBar() {
        var progress = this.state.progress;
        var status = this.state.serverStatus;
        var phase = this.state.phase;
        var result = this.state.result || {};
        var commitErrors = (result.commitStats && result.commitStats.errors) || [];
        var label = 'Starting...';
        if (phase === PHASE_UPLOADING) label = 'Uploading file...';
        else if (status === 'parsing') label = 'Parsing CSV...';
        else if (status === SERVER_STATUS_MATCHING) label = 'Matching against existing properties...';
        else if (status === SERVER_STATUS_COMMITTING) label = 'Writing to database...';
        else if (status === SERVER_STATUS_WRITING_NOTES) label = 'Writing property notes...';
        else if (status === SERVER_STATUS_FILING_OWNERS) label = 'Filing buyers and sellers as owners...';
        else if (phase === PHASE_PROCESSING) label = 'Processing...';

        return (
            <div className='progress-section'>
                <div className='progress-label'>{label} {progress}%</div>
                <div className='progress-bar-track'>
                    <div className='progress-bar-fill' style={{width: progress + '%'}}/>
                </div>
                {commitErrors.length > 0 &&
                    <div className='commit-error-banner'>
                        <strong>Errors during commit ({commitErrors.length}):</strong>
                        <ul>
                            {commitErrors.map(function (e, i) {
                                var loc = e.phase ? e.phase + (e.batch ? ' batch ' + e.batch : '') + (e.rowStart ? ' rows ' + e.rowStart + '-' + e.rowEnd : '') : '';
                                return <li key={i}>{loc ? loc + ': ' : ''}{e.error || JSON.stringify(e)}</li>;
                            })}
                        </ul>
                    </div>
                }
            </div>
        );
    }

    renderStats() {
        var r = this.state.result || {};
        var stats = r.stats || {};
        var total = r.total || 0;
        var validCount = r.validCount || 0;
        var rejectedCount = r.rejectedCount || 0;
        var warningsCount = r.warningsCount || 0;

        return (
            <React.Fragment>
                <div className='result-stats'>
                    <div className='stat'><span className='stat-number'>{total}</span><span className='stat-label'>Total Rows</span></div>
                    <div className={'stat ' + (validCount ? 'stat-success' : '')}>
                        <span className='stat-number'>{validCount}</span><span className='stat-label'>Valid</span>
                    </div>
                    <div className={'stat ' + (rejectedCount ? 'stat-error' : '')}>
                        <span className='stat-number'>{rejectedCount}</span><span className='stat-label'>Rejected</span>
                    </div>
                    <div className={'stat ' + (warningsCount ? 'stat-warning' : '')}>
                        <span className='stat-number'>{warningsCount}</span><span className='stat-label'>Warnings</span>
                    </div>
                </div>
                <div className='result-stats plan-stats'>
                    <div className={'stat ' + (stats.insertCount ? 'stat-success' : '')}>
                        <span className='stat-number'>{stats.insertCount || 0}</span><span className='stat-label'>Will Insert</span>
                    </div>
                    <div className={'stat ' + (stats.updateCount ? 'stat-info' : '')}>
                        <span className='stat-number'>{stats.updateCount || 0}</span><span className='stat-label'>Will Update</span>
                    </div>
                    <div className={'stat ' + (stats.skipCount ? 'stat-warning' : '')}>
                        <span className='stat-number'>{stats.skipCount || 0}</span><span className='stat-label'>Will Skip</span>
                    </div>
                </div>
                {(stats.buyerNeedOwnerCount || stats.buyerMatchedCount || stats.sellerOwnerMatchedCount) ?
                    <div className='result-stats plan-stats buyer-stats'>
                        <div className={'stat ' + (stats.buyerMatchedCount ? 'stat-success' : '')}>
                            <span className='stat-number'>{stats.buyerMatchedCount || 0}</span>
                            <span className='stat-label'>Buyers Matched</span>
                        </div>
                        <div className={'stat ' + (stats.sellerOwnerMatchedCount ? 'stat-success' : '')}>
                            <span className='stat-number'>{stats.sellerOwnerMatchedCount || 0}</span>
                            <span className='stat-label'>Sellers Matched</span>
                        </div>
                        <div className={'stat ' + (stats.buyerNeedOwnerCount ? 'stat-warning' : '')}>
                            <span className='stat-number'>{stats.buyerNeedOwnerCount || 0}</span>
                            <span className='stat-label'>Need Owner</span>
                        </div>
                    </div>
                    : null}
            </React.Fragment>
        );
    }

    renderActionBadge(action) {
        if (action === ACTION_INSERT) return <span className='chip chip-success'>INSERT</span>;
        if (action === ACTION_UPDATE) return <span className='chip chip-info'>UPDATE</span>;
        if (action === ACTION_SKIP) return <span className='chip chip-warn'>SKIP</span>;
        return null;
    }

    renderBuyerResolutionChip(code) {
        if (code === OWNER_CODE_MATCHED) return <span className='chip chip-success'>BUYER MATCHED</span>;
        if (code === OWNER_CODE_NO_MATCH) return <span className='chip chip-warn'>NOT MATCHED</span>;
        if (code === OWNER_CODE_AMBIGUOUS) return <span className='chip chip-warn'>SEVERAL MATCH</span>;
        if (code === OWNER_CODE_NO_NAME) return <span className='chip chip-warn'>NO BUYER</span>;
        return null;
    }

    renderPastOwnerChip(code) {
        if (code === OWNER_CODE_MATCHED) return <span className='chip chip-success'>PAST OWNER FOUND</span>;
        if (code === OWNER_CODE_NO_MATCH) return <span className='chip chip-info'>PAST OWNER NOT MATCHED</span>;
        if (code === OWNER_CODE_AMBIGUOUS) return <span className='chip chip-warn'>SEVERAL MATCH</span>;
        return null;
    }

    renderSellerResolutionChip(code) {
        if (code === SELLER_CODE_MATCHED) return <span className='chip chip-success'>SELLER MATCHED</span>;
        if (code === SELLER_CODE_WILL_CREATE) return <span className='chip chip-info'>NEW SELLER</span>;
        if (code === SELLER_CODE_GUARD) return <span className='chip chip-warn'>SELLER CONFLICT</span>;
        return null;
    }

    renderBuyerSellerResolution(p) {
        var parts = [];
        if (p.buyerResolution && p.buyerResolution.message) {
            parts.push(
                <div key='buyer' className='resolution-line'>
                    {this.renderBuyerResolutionChip(p.buyerResolution.code)}{' '}
                    <span className='resolution-text'>{p.buyerResolution.message}</span>
                </div>
            );
        }
        if (p.sellerOwnerResolution && p.sellerOwnerResolution.message && p.sellerOwnerResolution.code !== OWNER_CODE_NO_NAME) {
            parts.push(
                <div key='past-owner' className='resolution-line'>
                    {this.renderPastOwnerChip(p.sellerOwnerResolution.code)}{' '}
                    <span className='resolution-text'>{p.sellerOwnerResolution.message}</span>
                </div>
            );
        }
        if (p.sellerResolution && p.sellerResolution.message) {
            parts.push(
                <div key='seller' className='resolution-line'>
                    {this.renderSellerResolutionChip(p.sellerResolution.code)}{' '}
                    <span className='resolution-text'>{p.sellerResolution.message}</span>
                </div>
            );
        }
        if (!parts.length) return null;
        return <div className='resolution-block'>{parts}</div>;
    }

    renderCrmWarningBanner(stats) {
        stats = stats || {};
        var needOwner = stats.buyerNeedOwnerCount || 0;
        if (!needOwner) return null;

        return (
            <div className='comps-crm-warning'>
                <p>
                    <strong>{needOwner}</strong> propert{needOwner === 1 ? 'y' : 'ies'} will be flagged{' '}
                    <strong>NEED_OWNER</strong> (no buyer in the file, or nobody in Kore with that name and one more
                    matching detail). No buyer or seller is added to Kore from this upload.
                </p>
            </div>
        );
    }

    renderBuyerSellerPreviewSection(plan) {
        var rows = (plan || []).filter(function (p) {
            return (p.buyerResolution && p.buyerResolution.code === OWNER_CODE_NO_MATCH)
                || (p.buyerResolution && p.buyerResolution.code === OWNER_CODE_AMBIGUOUS);
        });
        if (!rows.length) return null;
        var self = this;
        return (
            <details className='result-details'>
                <summary className='details-toggle details-warning'>
                    Buyers requiring attention ({rows.length}) — nobody matched, or several match
                </summary>
                <div className='details-list'>
                    {rows.map(function (p) {
                        return (
                            <div key={'br-' + p.rowIndex} className='details-item details-warning-item'>
                                <div>
                                    <strong>Row {p.rowIndex}:</strong> {p.name || '(no name)'} &mdash; {p.address}, {p.city}
                                </div>
                                {self.renderBuyerSellerResolution(p)}
                            </div>
                        );
                    })}
                </div>
            </details>
        );
    }

    renderExistingSummary(existingSummary) {
        if (!existingSummary || !existingSummary.length) return null;
        return (
            <div className='details-meta'>
                Existing match{existingSummary.length > 1 ? 'es' : ''}:{' '}
                {existingSummary.map(function (e, i) {
                    var label = 'id ' + e.id + ' (' + (e.source || '-') + ' / ' + (e.status || '-') + (e.closeOfEscrow ? ' / COE ' + e.closeOfEscrow : '') + ')';
                    return <span key={e.id + '-' + i}>{i > 0 ? ', ' : ''}{label}</span>;
                })}
            </div>
        );
    }

    renderPlanGroup(title, action, plan, openByDefault) {
        var self = this;
        var rows = (plan || []).filter(function (p) { return p.action === action; });
        if (!rows.length) return null;
        return (
            <details className='result-details' open={openByDefault}>
                <summary className={'details-toggle ' + (action === ACTION_INSERT ? 'details-success' : action === ACTION_UPDATE ? 'details-info' : 'details-warning')}>
                    {title} ({rows.length})
                </summary>
                <div className='details-list'>
                    {rows.map(function (p) {
                        return (
                            <div key={p.rowIndex} className='details-item'>
                                <div>
                                    {self.renderActionBadge(p.action)}{' '}
                                    <strong>Row {p.rowIndex}:</strong> {p.name || '(no name)'} &mdash; {p.address || '(no address)'}, {p.city || '(no city)'}
                                    {p.targetRowId ? <span className='details-meta'> · target id {p.targetRowId}</span> : null}
                                    {p.hasNote ? <span className='details-meta'> · has note</span> : null}
                                </div>
                                <div className='details-reason'>{p.reason}</div>
                                {self.renderExistingSummary(p.existingSummary)}
                                {self.renderBuyerSellerResolution(p)}
                            </div>
                        );
                    })}
                </div>
            </details>
        );
    }

    renderRejected(rejected) {
        if (!rejected || !rejected.length) return null;
        return (
            <details open className='result-details'>
                <summary className='details-toggle details-error'>
                    Rejected rows ({rejected.length}) &mdash; not part of the plan
                </summary>
                <div className='details-list'>
                    {rejected.map(function (r) {
                        return (
                            <div key={r.rowIndex} className='details-item details-error-item'>
                                <div>
                                    <strong>Row {r.rowIndex}:</strong> {r.name || '(no name)'} &mdash; {r.address || '(no address)'}, {r.city || '(no city)'}
                                </div>
                                <ul className='details-reasons'>
                                    {(r.reasons || []).map(function (reason, ri) { return <li key={'r' + ri}>{reason}</li>; })}
                                    {(r.warnings || []).map(function (w, wi) { return <li key={'w' + wi} className='details-warning-inline'>{w}</li>; })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </details>
        );
    }

    renderWarnings(valid) {
        var rowsWithWarnings = (valid || []).filter(function (v) { return v.warnings && v.warnings.length > 0; });
        if (!rowsWithWarnings.length) return null;
        var total = rowsWithWarnings.reduce(function (sum, v) { return sum + v.warnings.length; }, 0);
        return (
            <details className='result-details'>
                <summary className='details-toggle details-warning'>
                    Warnings ({total}) &mdash; field-level notices on valid rows
                </summary>
                <div className='details-list'>
                    {rowsWithWarnings.map(function (v) {
                        return (
                            <div key={v.rowIndex} className='details-item details-warning-item'>
                                <div><strong>Row {v.rowIndex}:</strong> {v.name || '(no name)'} &mdash; {v.address}, {v.city}</div>
                                <ul>
                                    {v.warnings.map(function (w, wi) { return <li key={wi}>{w}</li>; })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </details>
        );
    }

    renderHeaderSection(unknownHeaders, ignoredHeaders) {
        var hasUnknown = unknownHeaders && unknownHeaders.length > 0;
        var hasIgnored = ignoredHeaders && ignoredHeaders.length > 0;
        if (!hasUnknown && !hasIgnored) return null;
        return (
            <details className='result-details' open={hasUnknown}>
                <summary className='details-toggle'>
                    Header handling{hasUnknown ? ' · ' + unknownHeaders.length + ' unknown' : ''}
                </summary>
                <div className='details-list'>
                    {hasUnknown &&
                        <div className='details-item details-warning-item'>
                            <div><strong>Unknown headers</strong> (in CSV but not mapped; will not be written):</div>
                            <div className='chip-row'>
                                {unknownHeaders.map(function (h) { return <span key={h} className='chip chip-warn'>{h}</span>; })}
                            </div>
                        </div>
                    }
                    {hasIgnored &&
                        <div className='details-item'>
                            <div><strong>Intentionally ignored</strong> (system-managed, HSC workflow, file attachments, etc.):</div>
                            <div className='chip-row'>
                                {ignoredHeaders.map(function (h) { return <span key={h} className='chip'>{h}</span>; })}
                            </div>
                        </div>
                    }
                </div>
            </details>
        );
    }

    renderPreview() {
        var r = this.state.result || {};
        return (
            <div className='result-section'>
                <div className='result-header result-dryrun'>
                    Dry-Run Preview &mdash; nothing has been written yet. Review the plan below, then click <strong>Commit to Database</strong>.
                </div>

                {this.renderStats()}

                {this.renderCrmWarningBanner(r.stats)}
                {this.renderBuyerSellerPreviewSection(r.plan)}

                {this.renderRejected(r.rejected)}
                {this.renderPlanGroup('Will Skip', ACTION_SKIP, r.plan, true)}
                {this.renderPlanGroup('Will Update', ACTION_UPDATE, r.plan, false)}
                {this.renderPlanGroup('Will Insert', ACTION_INSERT, r.plan, false)}
                {this.renderWarnings(r.valid)}
                {this.renderHeaderSection(r.unknownHeaders, r.ignoredHeaders)}

                <div className='commit-bar'>
                    <button type='button' className='button submit' onClick={this.commit}>Commit to Database</button>
                    <button type='button' className='button' onClick={this.resetUpload}>Cancel</button>
                </div>
            </div>
        );
    }

    renderCommitErrors(errors) {
        if (!errors || !errors.length) return null;
        return (
            <details open className='result-details'>
                <summary className='details-toggle details-error'>
                    Errors during commit ({errors.length})
                </summary>
                <div className='details-list'>
                    {errors.map(function (e, i) {
                        var loc = e.phase ? (e.phase + (e.batch ? ' batch ' + e.batch : '') + (e.rowStart ? ' rows ' + e.rowStart + '-' + e.rowEnd : '')) : '';
                        return (
                            <div key={i} className='details-item details-error-item'>
                                {loc ? <strong>{loc}: </strong> : null}{e.error || JSON.stringify(e)}
                            </div>
                        );
                    })}
                </div>
            </details>
        );
    }

    renderComplete() {
        var r = this.state.result || {};
        var cs = r.commitStats || {};
        var hadErrors = cs.errors && cs.errors.length > 0;
        return (
            <div className='result-section'>
                <div className={'result-header ' + (hadErrors ? 'result-error' : 'result-success')}>
                    Commit {hadErrors ? 'completed with errors' : 'complete'} &mdash; database updated.
                </div>
                <div className='result-stats plan-stats'>
                    <div className={'stat ' + (cs.inserted ? 'stat-success' : '')}>
                        <span className='stat-number'>{cs.inserted || 0}</span><span className='stat-label'>Inserted</span>
                    </div>
                    <div className={'stat ' + (cs.updated ? 'stat-info' : '')}>
                        <span className='stat-number'>{cs.updated || 0}</span><span className='stat-label'>Updated</span>
                    </div>
                    <div className={'stat ' + (cs.notesWritten ? 'stat-info' : '')}>
                        <span className='stat-number'>{cs.notesWritten || 0}</span><span className='stat-label'>Notes Written</span>
                    </div>
                    <div className={'stat ' + (cs.ownersFiled ? 'stat-info' : '')}>
                        <span className='stat-number'>{cs.ownersFiled || 0}</span><span className='stat-label'>Owners Filed</span>
                    </div>
                    <div className={'stat ' + ((r.stats && r.stats.skipCount) ? 'stat-warning' : '')}>
                        <span className='stat-number'>{(r.stats && r.stats.skipCount) || 0}</span><span className='stat-label'>Skipped</span>
                    </div>
                </div>
                {this.renderCommitErrors(cs.errors)}
                <div className='action-row'>
                    <button type='button' className='button primary' onClick={this.downloadSummary}>
                        Download Summary (.txt)
                    </button>
                    <button type='button' className='button ghost' onClick={this.resetUpload}>
                        Upload Another File
                    </button>
                </div>
            </div>
        );
    }

    renderFailed() {
        var r = this.state.result || {};
        var errors = r.errors || [];
        return (
            <div className='result-section'>
                <div className='result-header result-error'>Upload Failed</div>
                {errors.length > 0 &&
                    <div className='result-details'>
                        <div className='details-list'>
                            {errors.map(function (e, i) {
                                return <div key={i} className='details-item details-error-item'>{e.error || JSON.stringify(e)}</div>;
                            })}
                        </div>
                    </div>
                }
                <button type='button' className='button' onClick={this.resetUpload}>Upload Another File</button>
            </div>
        );
    }

    render() {
        var deleteIcon = 'url("' + arc.path.media + 'delete.svg")';
        var file = this.state.file;
        var phase = this.state.phase;
        var hasFile = Boolean(file.name) && !file.invalidUpload;
        var readyToSubmit = phase === PHASE_IDLE && hasFile && Boolean(file.encryption);
        var showProgress = phase === PHASE_UPLOADING || phase === PHASE_PROCESSING || phase === PHASE_COMMITTING;
        var showHelp = this.state.showHelp;
        var self = this;

        return (
            <div>
                <div className='upload-title-row'>
                    <div className='upload-file-title'>Upload Costar Comps CSV</div>
                    <button
                        type='button'
                        className='upload-help-btn'
                        onClick={function () { this.setState({ showHelp: true }); }.bind(this)}
                        title='How does this upload work?'
                    >
                        ?
                    </button>
                </div>

                <div className='upload-instructions'>
                    <p>
                        Upload a Costar comps CSV. The server first parses, normalizes, and matches each
                        row against existing properties, then shows a dry-run plan (insert / update / skip).
                        Nothing is written until you explicitly click <strong>Commit to Database</strong>.
                    </p>
                    <button type='button' className='button sample-btn' onClick={this.downloadSampleCsv}>
                        Download Sample Comps CSV
                    </button>
                </div>

                <Modal
                    show={showHelp}
                    onClose={function () { self.setState({ showHelp: false }); }}
                    className='upload-help-modal'
                >
                    <div className='upload-help-content'>
                        <div className='upload-help-header'>
                            <h2 className='upload-help-title'>Upload Comps &mdash; Help</h2>
                            <button
                                className='upload-help-close'
                                onClick={function () { self.setState({ showHelp: false }); }}
                            >&times;</button>
                        </div>
                        <div className='upload-help-body'>
                            <p>
                                Use this page to upload a Costar comps spreadsheet. The system
                                compares each row in your file against the properties already in
                                iCLIMB and decides what to do with it &mdash; add a new record,
                                update an existing one, or leave it alone. <strong>Nothing is saved
                                until you click Commit to Database.</strong>
                            </p>

                            <h3>How it works</h3>

                            <div className='upload-help-item'>
                                <h4>1. Download the sample CSV</h4>
                                <p>
                                    Click <strong>Download Sample Comps CSV</strong> to get a
                                    template with all the columns the system reads. Use it so your
                                    headers match what iCLIMB expects.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>2. Pick your file and submit</h4>
                                <p>
                                    Click <strong>Browse&hellip;</strong>, select your .csv file, then
                                    click <strong>Submit</strong>. The system reads every row, looks
                                    up matches in iCLIMB, and builds a plan. No changes are written
                                    yet.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>3. Review the dry-run plan</h4>
                                <p>
                                    You&rsquo;ll see summary cards for <strong>Will Insert</strong>,
                                    <strong> Will Update</strong>, and <strong>Will Skip</strong>.
                                    Open each section to see the exact rows and the reason for each
                                    decision. Rejected rows (ones the system couldn&rsquo;t read
                                    properly) are shown separately &mdash; those do not become part
                                    of the plan.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>4. Commit to the database</h4>
                                <p>
                                    When you&rsquo;re happy with the plan, click{' '}
                                    <strong>Commit to Database</strong>. Only at this step are the
                                    inserts, updates, and notes actually written to iCLIMB.
                                </p>
                            </div>

                            <div className='upload-help-item'>
                                <h4>5. Download the summary</h4>
                                <p>
                                    Once the commit finishes, click <strong>Download Summary
                                    (.txt)</strong> to save a text report of exactly what was
                                    inserted, updated, skipped, and rejected &mdash; with the real
                                    iCLIMB property IDs. Keep this file for your records.
                                </p>
                            </div>

                            <h3>What each decision means</h3>

                            <ul>
                                <li>
                                    <strong>Insert</strong> &mdash; the property isn&rsquo;t in
                                    iCLIMB yet (or the existing match is still Active, so we keep the
                                    live listing and add a new closed record for the comp).
                                </li>
                                <li>
                                    <strong>Update</strong> &mdash; a matching property exists with
                                    Status empty, <em>DB</em>, or <em>Property Info</em>. Its fields
                                    are refreshed from your file and the Status is moved to Closed.
                                    Existing notes are never overwritten; any new note from your
                                    file is added to the property&rsquo;s notes history.
                                </li>
                                <li>
                                    <strong>Skip</strong> &mdash; the row will not change anything
                                    in iCLIMB. This happens when:
                                    <ul>
                                        <li>
                                            the existing property has Source = <strong>Summit</strong>
                                            {' '}(those are never touched by an upload),
                                        </li>
                                        <li>
                                            the existing property is already Closed with a Close of
                                            Escrow date within <strong>45 days</strong> of the one in
                                            your file (treated as the same deal),
                                        </li>
                                        <li>
                                            more than one existing property matches the row &mdash;
                                            we skip it so you can review it by hand.
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Rejected</strong> &mdash; the row is unusable (for
                                    example, an unreadable Close of Escrow date or a missing
                                    address). Fix it in the file and upload again.
                                </li>
                            </ul>

                            <h3>How rows are matched</h3>
                            <p>
                                A row is matched to a property when{' '}
                                <strong>Address + City + Property Category + Property Type</strong>
                                {' '}all match. If nothing matches, the row is inserted as a new
                                property. If exactly one thing matches, the decision above decides
                                what to do. If more than one thing matches, the row is skipped as
                                ambiguous.
                            </p>

                            <h3>Defaults the system applies</h3>
                            <p>
                                Every uploaded comp is saved with <strong>Source = External</strong>
                                {' '}and <strong>Status = Closed</strong>. You don&rsquo;t need to
                                put those values in your file.
                            </p>

                            <h3>Keep in mind</h3>
                            <ul className='upload-help-tips'>
                                <li>
                                    <strong>Nothing</strong> changes in iCLIMB until you click{' '}
                                    <strong>Commit to Database</strong>. Feel free to cancel after
                                    the dry-run if something looks wrong.
                                </li>
                                <li>
                                    If a row is rejected for a bad value (often a Close of Escrow
                                    date), fix just that row in a new CSV and upload it again.
                                    Rows that were already committed will not be duplicated.
                                </li>
                                <li>
                                    Existing property notes are never deleted or overwritten. A note
                                    from your file is always added as a new entry in the{' '}
                                    <em>Notes</em> history.
                                </li>
                                <li>
                                    Always download the summary after a commit &mdash; it&rsquo;s
                                    your audit trail and lists the iCLIMB IDs of every affected
                                    property.
                                </li>
                                <li>
                                    For uploading new listings or general properties (not comps),
                                    use the <strong>Properties</strong> tab instead.
                                </li>
                            </ul>
                        </div>
                    </div>
                </Modal>

                {phase === PHASE_IDLE && !hasFile &&
                    <button type='button' className='button' onClick={this.uploadFile}>Browse...</button>
                }

                <div className='upload-file-details'>
                    {hasFile && phase === PHASE_IDLE && <div className='upload-file-text'>{file.name}</div>}
                    {file.invalidUpload && <div className='upload-file-text' style={{color: '#FF5050'}}>{file.message}</div>}
                    {hasFile && phase === PHASE_IDLE &&
                        <div onClick={this.removeFile} className='button delete' style={{backgroundImage: deleteIcon}}/>
                    }
                </div>

                {showProgress && this.renderProgressBar()}

                {phase === PHASE_PREVIEW && this.renderPreview()}
                {phase === PHASE_COMPLETE && this.renderComplete()}
                {phase === PHASE_FAILED && this.renderFailed()}

                {readyToSubmit &&
                    <button type='button' className='button submit' onClick={this.sendFile}>Submit</button>
                }
            </div>
        );
    }
}
